import type { ReactNode } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../primitives/Dialog";

export interface MerkleProofExplainerDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Plain-language one-liner that captures what the technical proof view proves.
 * Surfaced inline next to the disclosure toggle so the explanation is never
 * fully hidden. Deliberately avoids internal jargon.
 */
export const MERKLE_EXPLAINER_SUMMARY =
	"This shows, step by step, how this event combines with its neighbours to reproduce the record's published fingerprint, proving it was never changed.";

function MerkleProofExplainerDialog({
	open,
	onOpenChange,
}: MerkleProofExplainerDialogProps): ReactNode {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Understanding Merkle Proofs</DialogTitle>
					<DialogDescription>
						How an event's authenticity is proven, step by step.
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-3 text-sm text-foreground">
					<p>
						A merkle root is a single hash that cryptographically summarizes
						every event in a batch — if any event changes, the root changes too.
					</p>
					<p>
						When a batch is anchored on-chain, that root is written permanently
						to a public blockchain so it cannot be altered without detection.
					</p>
					<p>
						The highlighted path shows how your event hash combines step-by-step
						with sibling hashes to reach the published root.
					</p>
					<p>
						"Verified" here means the computed path matches the on-chain root,
						confirming your event is part of the original, unmodified batch.
					</p>
				</div>
			</DialogContent>
		</Dialog>
	);
}

MerkleProofExplainerDialog.displayName = "MerkleProofExplainerDialog";

export default MerkleProofExplainerDialog;
export { MerkleProofExplainerDialog };
